import { useTranslations } from '@vocab/react';
import {
  IconNewWindow,
  IconDelete,
  IconMail,
  IconShare,
  Text,
  IconPersonAdd,
  IconLocation,
  IconInfo,
} from 'braid-design-system';
import type React from 'react';

import translations from './.vocab';
import type { JobActionStatus } from './jobActionStatus';
import { JobAction } from './type';

export interface JobActionMenuOption {
  value: JobAction;
  label: string;
  inactive: boolean;
  inactiveDescription?: string;
  content: React.ReactNode;
  icon?: React.ReactNode;
}

export const useJobActionMenuOptions = (
  jobActionStatus: JobActionStatus,
): JobActionMenuOption[] => {
  const { t } = useTranslations(translations);

  return [
    {
      value: JobAction.VIEW,
      label: t('View job'),
      ...jobActionStatus.viewActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t('View your job ad')}
        </Text>
      ),
      icon: <IconNewWindow tone="secondary" />,
    },
    {
      value: JobAction.VIEW_JOB_INFO,
      label: t('View info'),
      ...jobActionStatus.viewJobInfoActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t('Details on how your job ad was posted')}
        </Text>
      ),
      icon: <IconInfo tone="secondary" />,
    },
    {
      value: JobAction.MULTI_LOCATION,
      label: t('Add locations'),
      ...jobActionStatus.multiLocationActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t('Post your job ad to more locations')}
        </Text>
      ),
      icon: <IconLocation tone="secondary" />,
    },
    {
      value: JobAction.NOTIFICATION_PREFERENCE,
      label: t('Notifications'),
      ...jobActionStatus.notificationActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t('Set how often you receive email notifications for this job')}
        </Text>
      ),
      icon: <IconMail tone="secondary" />,
    },
    {
      value: JobAction.TEAM_MEMBER_ACCESS,
      label: t('Team access'),
      ...jobActionStatus.teamAccessActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t('Give access to your team and invite them to this job')}
        </Text>
      ),
      icon: <IconPersonAdd tone="secondary" />,
    },
    {
      value: JobAction.EXPIRE,
      label: t('Expire'),
      ...jobActionStatus.expireActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t(
            'Remove your job from SEEK - you can continue to manage your candidates as normal',
          )}
        </Text>
      ),
      icon: <IconDelete tone="secondary" />,
    },
    {
      value: JobAction.SHARE,
      label: t('Share job'),
      ...jobActionStatus.shareActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t('Share job with others')}
        </Text>
      ),
      icon: <IconShare tone="secondary" />,
    },
  ];
};
